import { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { getLatestPublishedReleaseNoteTemplate } from 'apollo/templates/release-notes';
import { ReleaseNote } from 'models/release-note';
import { getShortLanguageCode } from 'utils/format';
import { CONSTANTS } from 'config/constants';
import { resolveOrgType, resolveContent } from 'utils/release-note-utils';

const COOKIE_EXPIRES_DAYS = 400;

const getCookieName = (userId: string) => `${CONSTANTS.RELEASE_NOTE_COOKIE_NAME_PREFIX}${userId}`;

const getLastSeenReleaseNoteId = (userId: string): string | null => Cookies.get(getCookieName(userId)) ?? null;

const deleteLastSeenReleaseNoteCookie = (userId: string): void => {
    Cookies.remove(getCookieName(userId), CONSTANTS.COOKIE_OPTIONS);
};

const setLastSeenReleaseNoteId = (userId: string, releaseNoteId: string): void => {
    deleteLastSeenReleaseNoteCookie(userId);
    Cookies.set(getCookieName(userId), releaseNoteId, { ...CONSTANTS.COOKIE_OPTIONS, expires: COOKIE_EXPIRES_DAYS });
};

type UseReleaseNotePopupResult = {
    isOpen: boolean;
    releaseNote: ReleaseNote | null;
    content: string;
    handleClose: () => void;
    handleViewAllReleaseNotes: () => void;
};

export const useReleaseNotePopup = (): UseReleaseNotePopupResult => {
    const navigate = useNavigate();
    const currentUser = currentUserVar();
    const currentOrg = currentOrgVar();
    const [isOpen, setIsOpen] = useState(false);
    const [releaseNote, setReleaseNote] = useState<ReleaseNote | null>(null);

    const { data } = useQuery<{ latestPublishedReleaseNote: ReleaseNote | null }>(
        getLatestPublishedReleaseNoteTemplate,
        {
            variables: { organizationType: resolveOrgType(currentOrg.type) },
            fetchPolicy: 'network-only',
            skip: !currentUser._id,
        },
    );

    useEffect(() => {
        const fetchedNote = data?.latestPublishedReleaseNote;
        if (!fetchedNote || !currentUser._id) return;

        const lastSeenId = getLastSeenReleaseNoteId(currentUser._id);

        if (lastSeenId !== fetchedNote._id) {
            setReleaseNote(fetchedNote);
            setIsOpen(true);
        }
    }, [data, currentUser._id]);

    const handleClose = () => {
        if (releaseNote && currentUser._id) {
            setLastSeenReleaseNoteId(currentUser._id, releaseNote._id);
        }
        setIsOpen(false);
    };

    const handleViewAllReleaseNotes = () => {
        handleClose();
        navigate('/release-notes-history');
    };

    const userLanguage = getShortLanguageCode(currentUser.localeFormat?.language);
    const content = releaseNote ? resolveContent(releaseNote, userLanguage) : '';

    return { isOpen, releaseNote, content, handleClose, handleViewAllReleaseNotes };
};
